<template>
    <v-container class="pa-0" fluid>
        <v-layout row>
            <v-flex xs2 class="px-1">
                <v-autocomplete
                    label="Plant"
                    :items="plants"
                    :value="selectedPlantId"
                    @input="handlePlantSelected"
                    hide-details
                    dense
                    outline
                    :read-only="plants.length == 1">
                </v-autocomplete>
            </v-flex>
            <v-flex xs2 class="px-1" v-if="plantSelected">
                <v-autocomplete
                    label="Category"
                    :items="categories"
                    :value="selectedCategoryId"
                    @input="handleCategorySelected"
                    hide-details
                    dense
                    outline
                    :read-only="categories.length == 1">
                </v-autocomplete>
            </v-flex>
            <v-flex xs3 class="px-1" v-if="canSelectDivision">
                <v-autocomplete
                    label="Division"
                    :items="availableDivisions"
                    :value="filterDivisionIds"
                    @input="handleDivisionSelected"
                    multiple
                    hide-details
                    dense
                    outline>
                    <template v-slot:selection="{ parent, item, index, selected, disabled }">
                        <span v-if="index == 0 ">{{ filterDivisionIds.length }} selected</span>
                    </template>
                </v-autocomplete>
            </v-flex>
            <v-flex xs3 class="px-1" v-if="canSelectDivision">
                <v-autocomplete
                    label="Locations"
                    :items="availableLocations"
                    :value="filterLocationIds"
                    @input="handleLocationSelected"
                    multiple
                    hide-details
                    dense
                    outline>
                    <template v-slot:selection="{ parent, item, index, selected, disabled }">
                        <span v-if="index == 0 ">{{ filterLocationIds.length }} selected</span>
                    </template>
                </v-autocomplete>
            </v-flex>
            <v-flex xs3 class="px-1" v-if="canSelectVariety">
                <v-autocomplete
                    label="Variety"
                    :items="availableVarieties"
                    :value="filterVarietyIds"
                    @input="handleVarietySelected"
                    multiple
                    hide-details
                    dense
                    outline>
                    <template v-slot:selection="{ parent, item, index, selected, disabled }">
                        <span v-if="index == 0 ">{{ filterVarietyIds.length }} selected</span>
                    </template>
                </v-autocomplete>
            </v-flex>
            <v-flex xs2 class="px-1" v-if="canSelectUOM">
                <v-autocomplete                                     
                    label="UOM"
                    :items="$Constants.HARVESTPLAN_UOM"
                    item-value="ID"
                    item-text="Name"
                    :value="selectedUOM"
                    @input="handleUOMSelected"
                    hide-details
                    dense
                    outline>
                </v-autocomplete>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import UtilService from '@/services/utilService.js'

import { mapState, mapMutations, mapActions } from "vuex"

export default {
    components: {

    },
    props: {
        routePlantId: {
            type: Number,
            required: false
        },
        routeProductCategoryId: {
            type: Number,
            required: false
        },
        routeCropLocationId: {
            type: Number,
            required: false
        },
        routeVarietyId: {
            type: Number,
            required: false
        }
    },
    data(){
        return {

        };
    },
    computed: {
        ...mapState("ForecastingStore", [
            "plantData",
            "selectedPlantId",
            "selectedCategoryId",
            "selectedUOM",
            "crops",
            "varieties",
            "filterDivisionIds",
            "filterVarietyIds",
            "filterLocationIds"
        ]),
        plants(){
            return this.plantData.map(x => {
                return {
                    text: x.plantName,
                    value: x.plantId
                };
            }).sort(UtilService.sortAscending(x => x.text));
        },
        categories(){
            let plant = this.plantData.find(x => x.plantId === this.selectedPlantId);
            if(plant == null){
                return [];
            }

            return plant.productCategories.map(x => {
                return {
                    text: x.productCategoryName,
                    value: x.productCategoryId,
                    uom: x.defaultUOM
                };
            }).sort(UtilService.sortAscending(x => x.text));
        },
        canSelectDivision(){
            return this.crops.length > 0;
        },
        availableDivisions(){
            return this.crops.reduce((a, x) => {
                if(!a.some(y => y.value == x.value)){
                    a.push({
                        text: x.divisionName,
                        value: x.divisionId
                    });
                }

                return a;
            }, []).sort(UtilService.sortAscending(x => x.text));
        },
        availableLocations(){
            // show all locations if no division is selected, otherwise filter by selected divisions
            var targetCrops = this.filterDivisionIds.length == 0 
                ? this.crops 
                : this.crops.filter(crop => this.filterDivisionIds.includes(crop.divisionId));

            return targetCrops.reduce((locations, crop) => {
                if (!locations.some(location => location.value === crop.locationId)) {
                    locations.push({
                        text: crop.locationName,
                        value: crop.locationId,
                    });
                }
                return locations;
            }, []).sort(UtilService.sortAscending(location => location.text));
        },
        canSelectVariety(){
            return this.varieties.length > 0;
        },
        canSelectUOM() {
            return this.categorySelected;
        },
        availableVarieties(){
            return this.varieties.reduce((a, x) => {
                if(!a.some(y => y.value == x.value)){
                    a.push({
                        text: x.varietyName,
                        value: x.varietyId
                    });
                }

                return a;
            }, []).sort(UtilService.sortAscending(x=> x.text));
        },
        plantSelected(){
            return this.selectedPlantId != null;
        },
        categorySelected(){
            return this.selectedCategoryId != null;
        }
    },
    methods: {
        ...mapMutations("ForecastingStore", [
            "setSelectedPlantId",
            "setSelectedCategoryId",
            "setFilterDivisionIds",
            "setFilterLocationIds",
            "setFilterVarietyIds",
            "setFilterLocationName",
            "setFilterUOM"
        ]),
        ...mapActions("ForecastingStore", [
            "loadWeatherData",
            "loadCropVarieties",
            "updateData",
            "clearCropVarieties"
        ]),
        async handlePlantSelected(value){
            this.setSelectedPlantId(value);
            this.clearDivisionAndVarietyFilters();
            if(this.selectedPlantId != null) {
                // Weather data is not used in any subsequent data so it can be loaded separately
                this.loadWeatherData();
            }

            this.$nextTick(() => {
                if(this.categories.length == 1){
                    this.handleCategorySelected(this.categories[0].value)
                } else if(!this.categories.some(x => x.value == this.selectedCategoryId)){
                    this.handleCategorySelected(null);
                } else {
                    this.handleCategorySelected(this.selectedCategoryId)
                }
            });
        },
        async handleCategorySelected(value){
            this.setSelectedCategoryId(value);
            this.clearDivisionAndVarietyFilters();
            if(this.selectedPlantId != null && this.selectedCategoryId != null) {
                this.setFilterLocationName(null);

                // Set the default UOM
                let category = this.categories.find(c => c.value == this.selectedCategoryId);
                this.setFilterUOM(category.uom);

                await this.loadCropVarieties();
                await this.updateData();
            } else {
                await this.clearCropVarieties();
                await this.updateData();
            }
        },
        async handleDivisionSelected(value){
            this.setFilterDivisionIds(value);
            await this.updateData();
        },
        async handleLocationSelected(value){
            this.setFilterLocationIds(value);
            await this.updateData();
        },
        async handleVarietySelected(value){
            this.setFilterVarietyIds(value);
            await this.updateData();
        },
        async handleUOMSelected(value) {
            this.setFilterUOM(value);
            await this.updateData();
        },
        async jumpToRoute(){
            if(this.routePlantId && this.routeProductCategoryId) {
                await this.handlePlantSelected(this.routePlantId);
                await this.handleCategorySelected(this.routeProductCategoryId);
                if(this.routeCropLocationId && this.routeVarietyId) {
                    this.$emit("goToDetail", {cropLocationId: this.routeCropLocationId, varietyId: this.routeVarietyId});
                }
            }
        },
        clearDivisionAndVarietyFilters() {
            this.setFilterDivisionIds([]);
            this.setFilterLocationIds([]);
            this.setFilterVarietyIds([]);
        }
    },
    created(){
        if(this.plants.length == 1){
            this.handlePlantSelected(this.plants[0].value)
        } else if(!this.plants.some(x => x.value == this.selectedPlantId)){
            this.handlePlantSelected(null);
        }
    },
    mounted(){
        this.jumpToRoute();
    }
}
</script>